<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.GUESTS.title"
    :icon="CommonService.routes.GUESTS.icon"
    :loading="loading"
    flexBody
  >
    <!-- Filters -->
    <NiceFilters v-model="filtersList" @change="filterChanged" :showCreateButton="permissions.can_create_guests">
      <!-- Refresh -->
      <NiceButton icon="icon-refresh-cw" :title="$gettext('Refresh')" @click="getData" plain :loading="loading"></NiceButton>

      <!-- More -->
      <NicePopup v-if="permissions.can_create_guests">
        <template #trigger>
          <NiceButton type="primary" icon="icon-more-vertical" />
        </template>
        <template #content>
          <div class="d-grid gap-2 text-left">
            <!-- All PDF Tickets -->
            <NiceButton icon="icon-download-cloud" :text="$gettext('All PDF Tickets')" @click="downloadPDFTickets"></NiceButton>

            <!-- Download import template -->
            <NiceButton
              icon="icon-download-cloud"
              :text="$gettext('Download CSV template')"
              :loading="exportingFile"
              @click="downloadCsvTemplate"
            ></NiceButton>

            <!-- Export CSV -->
            <NiceButton icon="icon-download-cloud" :text="$gettext('Export CSV')" :loading="exportingFile" @click="exportCSV"></NiceButton>

            <!-- Import -->
            <NiceButton icon="icon-upload-cloud" :text="$gettext('Import')" @click="openImport"></NiceButton>
          </div>
        </template>
      </NicePopup>
    </NiceFilters>

    <!-- Table -->
    <NiceTable
      :actions="actions"
      :columns="columns"
      :data="data"
      :loading="loading"
      freezeLastColumn
      showFooter
      showLimit
      paginated
      @filterChange="filterChanged"
    />

    <!-- Form -->
    <NicePanel :name="panelName" @close="closeEdit">
      <GuestForm @cancel="closeEdit" @success="successEdit" :data="selectedItem"></GuestForm>
    </NicePanel>

    <!-- Confirm activate -->
    <NiceConfirmModal name="confirm-activate" @confirm="activateGuest" :title="$gettext('Activate')">
      <p class="p-0 m-0 mb-3">
        {{ $gettext("Are you sure you want to activate '%{name}'?", { name: guestToActivateName }) }}
      </p>
      <NiceSwitch v-if="guestToActivate.email" :title="$gettext('Send email')" v-model="activateForm.send_email" noMargin></NiceSwitch>
    </NiceConfirmModal>

    <!-- Confirm deactivate -->
    <NiceConfirmModal name="confirm-deactivate" @confirm="deactivateGuest" :title="$gettext('Deactivate')">
      <p class="p-0 m-0 mb-3">
        {{ $gettext("Are you sure you want to deactivate '%{name}'?", { name: guestToActivateName }) }}
      </p>
    </NiceConfirmModal>

    <!-- Confirm send ticket -->
    <NiceConfirmModal name="confirm-send-ticket" @confirm="sendTicket" :title="$gettext('Send ticket')">
      <p class="p-0 m-0 mb-3">
        {{ $gettext("Are you sure you want to send ticket to '%{name}'?", { name: guestToActivateName }) }}
      </p>
    </NiceConfirmModal>

    <!-- PDF Viewier -->
    <PDFViewerModal :blob="pdfBlob" :loading="pdfBlobLoading" width="95vw" height="95vh"></PDFViewerModal>

    <!-- Bulk send invoice links -->
    <ImportGuests @success="getData"></ImportGuests>

    <!-- <pre>{{ data }}</pre> -->
  </NiceView>
</template>

<script setup>
import { ref, inject, watch, onMounted, computed } from "vue";
const $getQuery = inject("getQuery");
const $nice = inject("nice");

import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import ApiService from "@/services/api/api.service";

import GuestForm from "@/components/forms/GuestForm.vue";
import PDFViewerModal from "@/components/modals/PDFViewerModal.vue";
import ImportGuests from "@/components/modals/ImportGuests.vue";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
import { formatDateWithTime } from "@/plugins/date";
const generalStore = useGeneralStore();
const permissions = computed(() => generalStore.permissions);
const modules = computed(() => generalStore.modules);

const data = ref([]);
const loading = ref(false);
const exportingFile = ref(false);
const selectedItem = ref(null);
const guestToActivate = ref(null);
const guestToActivateName = computed(() => (guestToActivate.value ? `${guestToActivate.value.first_name} ${guestToActivate.value.last_name}` : ""));
const pdfBlob = ref(null);
const pdfBlobLoading = ref(false);
const panelName = "event";
const activateForm = ref({
  send_email: false,
});

const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

// Watch for id change
watch(
  () => route.query,
  () => {
    if (route.query.id) {
      $nice.panel(panelName, true);
    }
  }
);

onMounted(async () => {
  if (route.query.id) {
    $nice.panel(panelName, true);
  }
});

function closeEdit() {
  selectedItem.value = null;
  router.push({ query: { ...route.query, id: undefined } });
  $nice.panel(panelName, false);
}

function successEdit() {
  closeEdit();
  getData();
}

function openEdit(item) {
  selectedItem.value = item;
  router.push({ query: { ...route.query, id: item.id } });
}

// function openCheckins(item) {
//   router.push({ name: "app.organizations.events.checkin-logs", query: { ...route.query, guest: item.id } });
// }

async function downloadPDFTickets() {
  pdfBlobLoading.value = true;
  $nice.modal("pdf-viewer", true);
  try {
    const query = await $getQuery();
    delete query.limit;
    delete query.offset;
    delete query.page;
    const response = await ApiService.guests.getAllTicketsPDF(route.params.organizationId, route.params.eventId, query);
    pdfBlob.value = URL.createObjectURL(response);
  } catch (e) {
    ErrorService.showError(e);
    $nice.modal("pdf-viewer", false);
  } finally {
    pdfBlobLoading.value = false;
  }
}

async function downloadCsvTemplate() {
  exportingFile.value = true;
  try {
    const response = await ApiService.guests.downloadImportTemplate(route.params.organizationId, route.params.eventId);
    CommonService.downloadBlob(response, "guests-import-template.csv");
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    exportingFile.value = false;
  }
}

async function askToSendTicket(item, e) {
  guestToActivate.value = item;
  $nice.modal("confirm-send-ticket", true, e);
}

async function sendTicket() {
  guestToActivate.value.loading = true;
  try {
    await ApiService.guests.sendTicket(route.params.organizationId, route.params.eventId, guestToActivate.value.id);
    guestToActivate.value.is_ticket_email_sent = true;
    CommonService.success($gettext("Ticket PDF was sent to '%{name}'!", { name: guestToActivateName.value }));
    $nice.modal("confirm-send-ticket", false);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    guestToActivate.value.loading = false;
  }
}

async function askToActivateGuest(item, e) {
  guestToActivate.value = item;
  $nice.modal("confirm-activate", true, e);
}

async function activateGuest() {
  guestToActivate.value.loading = true;
  try {
    await ApiService.guests.activate(route.params.organizationId, route.params.eventId, guestToActivate.value.id, activateForm.value);
    guestToActivate.value.status = "ACTIVE";
    CommonService.success($gettext("Guest '%{name}' was activated!", { name: guestToActivate.value.first_name }));
    $nice.modal("confirm-activate", false);
    getData();
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    guestToActivate.value.loading = false;
  }
}

async function askToDeactivateGuest(item, e) {
  guestToActivate.value = item;
  $nice.modal("confirm-deactivate", true, e);
}

async function deactivateGuest() {
  guestToActivate.value.loading = true;
  try {
    await ApiService.guests.deactivate(route.params.organizationId, route.params.eventId, guestToActivate.value.id);
    guestToActivate.value.status = "DEACTIVATED";
    CommonService.success($gettext("Guest '%{name}' was deactivated!", { name: guestToActivate.value.first_name }));
    $nice.modal("confirm-deactivate", false);
    getData();
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    guestToActivate.value.loading = false;
  }
}

async function openTicket(item) {
  pdfBlobLoading.value = true;
  $nice.modal("pdf-viewer", true);
  try {
    const response = await ApiService.guests.getTicketPDF(route.params.organizationId, route.params.eventId, item.id);
    pdfBlob.value = URL.createObjectURL(response);
  } catch (e) {
    ErrorService.showError(e);
    $nice.modal("pdf-viewer", false);
  } finally {
    pdfBlobLoading.value = false;
  }
}

function openImport() {
  $nice.modal("import-guests", true);
}

async function exportCSV() {
  exportingFile.value = true;
  try {
    const response = await ApiService.guests.export(organizationId, eventId);
    const filename = `Guests-${generalStore.event.name}-${generalStore.organization.name}.csv`;
    CommonService.downloadFile(response, filename);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    exportingFile.value = false;
  }
}

async function getData() {
  loading.value = true;
  try {
    const query = await $getQuery();
    data.value = await ApiService.guests.getAll(route.params.organizationId, route.params.eventId, query);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

const filtersList = [
  {
    name: $gettext("Status"),
    type: "select",
    key: "status",
    valueName: "name",
    searchFunction: () => CommonService.guestStatus,
  },
  {
    name: $gettext("Checked in"),
    type: "yesno",
    key: "is_checked_in",
  },
  {
    name: $gettext("Group"),
    type: "select",
    key: "group",
    valueName: "name",
    searchFunction: searchGroups,
  },
  modules.value.PARTNERS
    ? {
        name: $gettext("Partner"),
        type: "select",
        key: "partner",
        valueName: "name",
        searchFunction: searchPartners,
      }
    : {},
];

function filterChanged() {
  getData();
}

async function searchGroups(keyword) {
  return ApiService.groups.getAll(organizationId, eventId, { search: keyword });
}

async function searchPartners(keyword) {
  return ApiService.partners.getAll(organizationId, eventId, { search: keyword });
}

const actions = [
  // {
  //   icon: () => "icon-clipboard",
  //   title: () => $gettext("Checkins"),
  //   function: (v) => openCheckins(v),
  //   hidden: (v) => v.type == "PENDING",
  // },
  {
    icon: () => "icon-file-text",
    title: () => $gettext("Ticket PDF"),
    function: (v) => openTicket(v),
    hidden: (v) => v.status == "PENDING" || !!v.deactivated_by,
  },
  {
    icon: () => "icon-send",
    title: () => $gettext("Send ticket"),
    function: (v) => askToSendTicket(v, event),
    disabled: (v) => !!v.deactivated_by || v.is_ticket_email_sent || v.email == null,
    hidden: (v) => v.status == "PENDING" || !modules.value.SEND_TICKETS_TO_GUESTS,
    loading: () => (guestToActivate.value ? guestToActivate.value.loading : false),
  },
  {
    icon: () => "icon-x",
    title: () => $gettext("Deactivate"),
    function: (v) => askToDeactivateGuest(v, event),
    type: () => "danger",
    hidden: (v) => v.status != "PENDING" || !permissions.value.can_confirm_guests,
    loading: () => (guestToActivate.value ? guestToActivate.value.loading : false),
  },
  {
    icon: () => "icon-check",
    title: () => $gettext("Activate"),
    function: (v) => askToActivateGuest(v, event),
    type: () => "success",
    hidden: (v) => v.status != "PENDING" || !permissions.value.can_confirm_guests,
    loading: () => (guestToActivate.value ? guestToActivate.value.loading : false),
  },
  {
    icon: () => "icon-edit-3",
    function: (v) => openEdit(v),
    hidden: () => !permissions.value.can_edit_guests,
  },
];

const columns = [
  // {
  //   key: "indicator",
  //   class: "w-0 pr-1",
  //   fieldClass: (v, row) => {
  //     return row.is_checked_in ? "nice-indicator active" : "nice-indicator";
  //   },
  // },
  {
    name: $gettext("Name"),
    key: "first_name",
    html: (v, row) => `
      <div class="d-flex f-center gap-2">
        <div class="${row.deactivated_by ? "strikethrough" : ""}">${row.first_name} ${row.last_name}</div>
        ${modules.value.PLUS_ONES && row.plus_one_count > 0 ? `<div class="badge badge-primary">+${row.plus_one_count}</div>` : ""}
      </div>
    `,
    hasOrder: true,
  },
  {
    name: $gettext("Company"),
    key: "company_name",
    formatter: (v) => (v ? v : "-"),
    hasSearch: true,
    hasOrder: true,
  },
  {
    name: $gettext("Email"),
    key: "email",
    formatter: (v) => (v ? v : "-"),
    hasOrder: true,
  },
  {
    name: $gettext("Phone"),
    key: "phone_number",
    formatter: (v) => (v ? v : "-"),
    hasOrder: true,
  },
  // {
  //   name: $gettext("Plus one"),
  //   key: "plus_one_count",
  //   class: "text-right",
  //   disabled: !modules.value.PLUS_ONES,
  //   html: (v) => `<div class="badge badge-primary">+${v}</div>`,
  // },
  {
    name: $gettext("Group"),
    key: "group",
    class: "text-center",
    html: (v) => `<span class="badge" style="background-color: ${v.color};">${v.name}</span>`,
    hasOrder: true,
  },
  {
    name: $gettext("Status"),
    key: "status",
    html: (v, row) => `
      <span class="badge ${CommonService.guestStatusMap[v].class}">${CommonService.guestStatusMap[v].name}</span>
      ${row.deactivated_timestamp ? `<small class="text-muted">${formatDateWithTime(row.deactivated_timestamp, true)}</small>` : ""}
    `,
    hasOrder: true,
  },
  {
    name: $gettext("Partner"),
    key: "partner",
    html: (v) => (v ? `${v.name}` : "-"),
    hasOrder: true,
  },
  // {
  //   name: "",
  //   key: "deactivated_by",
  //   html: (v) => `${v ? v.first_name + " " + v.last_name : ""}`,
  // },
  // {
  //   name: $gettext("Added"),
  //   key: "created",
  //   class: "text-right",
  //   formatter: (v) => formatDateWithTime(v),
  // },
];
</script>
